import { useState, useCallback, useMemo } from "react";
import { parseBooleanQuery, extractSearchTerms } from "../utils/booleanSearchParser";
import type { ParseResult } from "../utils/booleanSearchParser";

const STORAGE_KEY = "skyhire-boolean-search";

function readStoredMode(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

export interface UseBooleanSearchReturn {
  isBooleanMode: boolean;
  toggleBooleanMode: () => void;
  validateQuery: (query: string) => ParseResult;
  /** Minimum search term content length met (ignoring operators) */
  meetsMinLength: (query: string, minLength?: number) => boolean;
  syntaxError: string | null;
  setSyntaxError: (error: string | null) => void;
}

export function useBooleanSearch(): UseBooleanSearchReturn {
  const [isBooleanMode, setIsBooleanMode] = useState(readStoredMode);
  const [syntaxError, setSyntaxError] = useState<string | null>(null);

  const toggleBooleanMode = useCallback(() => {
    setIsBooleanMode((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        // localStorage unavailable
      }
      if (!next) setSyntaxError(null);
      return next;
    });
  }, []);

  const validateQuery = useCallback((query: string): ParseResult => {
    return parseBooleanQuery(query);
  }, []);

  const meetsMinLength = useCallback((query: string, minLength = 2): boolean => {
    const terms = extractSearchTerms(query);
    return terms.length >= minLength;
  }, []);

  return useMemo(
    () => ({
      isBooleanMode,
      toggleBooleanMode,
      validateQuery,
      meetsMinLength,
      syntaxError,
      setSyntaxError,
    }),
    [isBooleanMode, toggleBooleanMode, validateQuery, meetsMinLength, syntaxError],
  );
}
